// MD5 for the Content-MD5 header S3 checks every uploaded part against. Browsers have no MD5 of their own, so this
// one is shared: it takes the data in pieces of any size and holds only one 64-byte block between them.

const SHIFTS = Uint8Array.of(
  7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20,
  4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21,
);

/** floor(abs(sin(i + 1)) * 2^32), written out as RFC 1321 lists them. */
const SINES = Int32Array.of(
  0xd76aa478, 0xe8c7b756, 0x242070db, 0xc1bdceee, 0xf57c0faf, 0x4787c62a, 0xa8304613, 0xfd469501, 0x698098d8, 0x8b44f7af, 0xffff5bb1,
  0x895cd7be, 0x6b901122, 0xfd987193, 0xa679438e, 0x49b40821, 0xf61e2562, 0xc040b340, 0x265e5a51, 0xe9b6c7aa, 0xd62f105d, 0x02441453,
  0xd8a1e681, 0xe7d3fbc8, 0x21e1cde6, 0xc33707d6, 0xf4d50d87, 0x455a14ed, 0xa9e3e905, 0xfcefa3f8, 0x676f02d9, 0x8d2a4c8a, 0xfffa3942,
  0x8771f681, 0x6d9d6122, 0xfde5380c, 0xa4beea44, 0x4bdecfa9, 0xf6bb4b60, 0xbebfbc70, 0x289b7ec6, 0xeaa127fa, 0xd4ef3085, 0x04881d05,
  0xd9d4d039, 0xe6db99e5, 0x1fa27cf8, 0xc4ac5665, 0xf4292244, 0x432aff97, 0xab9423a7, 0xfc93a039, 0x655b59c3, 0x8f0ccc92, 0xffeff47d,
  0x85845dd1, 0x6fa87e4f, 0xfe2ce6e0, 0xa3014314, 0x4e0811a1, 0xf7537e82, 0xbd3af235, 0x2ad7d2bb, 0xeb86d391,
);

/** Which word of the block each of the 64 steps reads. */
const WORD = Uint8Array.from({ length: 64 }, (_, i) => (i < 16 ? i : i < 32 ? (5 * i + 1) % 16 : i < 48 ? (3 * i + 5) % 16 : (7 * i) % 16));

const BASE64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

export interface Md5 {
  /** Adds the next piece of the data. */
  update(bytes: Uint8Array): Md5;
  /** The 16-byte digest of everything added. The hash takes no more data after this. */
  digest(): Uint8Array;
}

/** Starts an MD5 hash that takes the data in pieces. */
export function createMd5(): Md5 {
  const state = Int32Array.of(0x67452301, 0xefcdab89, 0x98badcfe, 0x10325476);
  const block = new Uint8Array(64);
  const words = new Int32Array(16);
  let filled = 0;
  let length = 0;
  let finished = false;

  const compress = (data: Uint8Array, at: number) => {
    for (let i = 0; i < 16; i++) {
      const o = at + i * 4;
      words[i] = data[o]! | (data[o + 1]! << 8) | (data[o + 2]! << 16) | (data[o + 3]! << 24);
    }
    let a = state[0]!;
    let b = state[1]!;
    let c = state[2]!;
    let d = state[3]!;
    for (let i = 0; i < 64; i++) {
      const f = i < 16 ? (b & c) | (~b & d) : i < 32 ? (d & b) | (~d & c) : i < 48 ? b ^ c ^ d : c ^ (b | ~d);
      const sum = (a + f + SINES[i]! + words[WORD[i]!]!) | 0;
      const s = SHIFTS[i]!;
      a = d;
      d = c;
      c = b;
      b = (b + ((sum << s) | (sum >>> (32 - s)))) | 0;
    }
    state[0] = (state[0]! + a) | 0;
    state[1] = (state[1]! + b) | 0;
    state[2] = (state[2]! + c) | 0;
    state[3] = (state[3]! + d) | 0;
  };

  const md5: Md5 = {
    update(bytes) {
      if (finished) throw new Error('This MD5 hash is already finished.');
      length += bytes.length;
      let at = 0;
      if (filled) {
        at = Math.min(64 - filled, bytes.length);
        block.set(bytes.subarray(0, at), filled);
        filled += at;
        if (filled < 64) return md5;
        compress(block, 0);
        filled = 0;
      }
      for (; at + 64 <= bytes.length; at += 64) compress(bytes, at);
      block.set(bytes.subarray(at), 0);
      filled = bytes.length - at;
      return md5;
    },
    digest() {
      if (finished) throw new Error('This MD5 hash is already finished.');
      finished = true;
      const bits = length * 8;
      block[filled++] = 0x80;
      if (filled > 56) {
        block.fill(0, filled);
        compress(block, 0);
        filled = 0;
      }
      block.fill(0, filled, 56);
      const tail = new DataView(block.buffer);
      tail.setUint32(56, bits >>> 0, true);
      tail.setUint32(60, Math.floor(bits / 2 ** 32), true);
      compress(block, 0);
      const out = new Uint8Array(16);
      const view = new DataView(out.buffer);
      for (let i = 0; i < 4; i++) view.setInt32(i * 4, state[i]!, true);
      return out;
    },
  };
  return md5;
}

/** Standard base64 with padding, the form a Content-MD5 header takes. */
export function toBase64(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const n = (bytes[i]! << 16) | ((bytes[i + 1] ?? 0) << 8) | (bytes[i + 2] ?? 0);
    out += BASE64[(n >> 18) & 63]! + BASE64[(n >> 12) & 63]!;
    out += i + 1 < bytes.length ? BASE64[(n >> 6) & 63]! : '=';
    out += i + 2 < bytes.length ? BASE64[n & 63]! : '=';
  }
  return out;
}
